/**
 * App-side wiring for the Work-home surfaces behind `@heidi/agents`.
 *
 * Every member crosses BY REFERENCE and is called where it was called before
 * the move: the landing's desktop-target picker is what enables the roster
 * query, the Library thumbnail's signed-URL read is scoped to a mounted card,
 * and the default-landing banner's preference read is gated on the rest of its
 * chain — resolving any of them here would move a query to a render that does
 * not happen today.
 *
 * IMPORTED FOR EFFECT BY `src/components/layout/scribe-layout.tsx`, not by the
 * Work route. The route is a Chat file, and an `@/utils` import there raises the
 * Chat ledger's zone count, which is a ruling rather than a migration. The
 * layout is the route's own static import and is evaluated before it renders.
 */
import { agentRecordingSessionIdAtom } from '@/atoms/agentRecordingAtoms';
import {
  hasBootLandedOnHomeAtom,
  isHomeBootPath,
  readBootPathname,
} from '@/atoms/bootLandingAtoms';
import { routesToChronicleAtom } from '@/atoms/chronicle';
import { formDraftTitleAtom, formTemplateToEditAtom } from '@/atoms/formFillingAtom';
import { pendingLettersAtomFamily } from '@/atoms/letterAtoms';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { useOfflinePendingServerDeleteIds } from '@/atoms/offline-recording';
import { sessionWindowsAtomFamily } from '@/atoms/sessionWindowsAtoms';
import {
  presentationAtom,
  presentationHistoryAtom,
  presentationOriginalAtom,
  selectedSlideIndexAtom,
} from '@/atoms/slideBuilderAtoms';
import { isShortcutsDisabledAtom } from '@/components/layout/ShortcutsHelper/shortcutsAtom';
import { DesktopBars } from '@/components/mic-selector/DesktopBars';
import { useSaveFormTemplate } from '@/components/modal/form-filling-modals/view-form-modal/useSaveFormTemplate';
import { FormViewType } from '@/components/modal/form-filling-modals/view-form-modal/view-form-modal.types';
import { usePresentation } from '@/components/slide-builder/hooks/usePresentation';
import { slideBuilderMessages } from '@/components/slide-builder/slide-builder.messages';
import {
  useSlideToolbarActions,
  useSlideUndoRedoHotkeys,
} from '@/components/slide-builder/useSlideToolbarActions';
import {
  clearCreditAllowanceRefusal,
  nudgeCreditAllowanceRefusal,
  rememberCreditAllowanceRefusal,
  shouldSkipCreditAllowanceFollowUpSend,
} from '@/components/ui/agent-credit-allowance-banner/creditAllowanceRefusalAtom';
import { getResolvedShortcutsForMarket } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import { buildSearchableShortcuts } from '@/components/ui/floating-ask-heidi/shortcut-search';
import { patientSexAge } from '@/composition/hosts/chat-object-reference-label';
import { useOpenPatientFullPage } from '@/composition/hosts/patients/patient-profile/useOpenPatientFullPage';
import { usePatientProfileHost } from '@/composition/hosts/patients/patient-profile/usePatientProfileHost';
import { GET_SESSIONS_LIST } from '@/constants/sessions';
import {
  focusedArtifactTab,
  toFocusedArtifactTarget,
} from '@/container/session-page/v2/main/focused-artifact-target';
import {
  loadSplitScreen,
  reportSplitScreenPreloadFailure,
} from '@/container/session-panel/split-screen-loader';
import {
  candidateMappingKey,
  candidateMappingsAtom,
  clearCandidateMappingsAtom,
  ingestFieldCandidatesAtom,
  ingestNotePushHintsAtom,
  ingestNotePushRecordedAtom,
  setSectionOverrideAtom,
} from '@/features/computer-use-note-push/candidate-mappings';
import { copilotRunContextByProjectAtom } from '@/features/copilot/copilot-run-sessions';
import {
  AGENT_HOME_V1_FLAG_KEY,
  useIsAgentHomeV1Enabled,
  useIsAskHeidiSourcesShortcutsEnabled,
  useIsChatObjectRefsEnabled,
  useIsDefaultLandingPageSettingEnabled,
  useIsPatientListEnabled,
  useIsProjectsEnabled,
  useIsRpaRecorderEnabled,
  useIsWorkflowsEnabled,
} from '@/features/flags';
import { useDefaultLandingPageBannerWaitMs } from '@/features/flags/default-landing-page-banner-wait';
import { useFormFieldEditorFlags } from '@/features/flags/form-field-editor-flags';
import {
  IS_CHAT_ATTACHMENTS_ENABLED,
  IS_INSET_DESIGN_ENABLED,
  IS_SLIDE_BUILDER_ENABLED,
} from '@/features/flags/legacy-flags';
import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import { WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY } from '@/features/flags/work-home-object-workspace-flag';
import { useLetter } from '@/features/integrations-letters/letter-sender-v2/components/letter-tab-pane/useLetter';
import { useQueryLetters } from '@/features/integrations-letters/letter-sender-v2/hooks/query/useQueryLetters';
import { useLetterSenderActions } from '@/features/integrations-letters/letter-sender-v2/hooks/useLetterSenderActions';
import { getAuthToken } from '@/features/rpa/agent-client/auth-token';
import { cancelComputerUseRun } from '@/features/rpa/agent-client/cancel-computer-use-run';
import {
  releaseDesktopControlApprovalReservation,
  releaseDesktopControlApprovalReservationsForAgentRun,
} from '@/features/rpa/desktop-control-approval-reservation';
import { subscribeDesktopControlLease } from '@/features/rpa/desktop-control-lease';
import { useDesktopPermissionGate } from '@/features/rpa/desktop-permission-gate';
import { isThisComputerTarget } from '@/features/rpa/desktop-target/desktopTargetSelection';
import { useNativeInstanceId } from '@/features/rpa/desktop-target/nativeInstanceId';
import { useDesktopTargetRoster } from '@/features/rpa/desktop-target/useDesktopTargetRoster';
import { useDesktopTargetSelection } from '@/features/rpa/desktop-target/useDesktopTargetSelection';
import { useExecutorStopSignal } from '@/features/rpa/executor/useExecutorStopSignal';
import {
  emitProjectAgentRegistryPoke,
  listenProjectAgentRegistryPoke,
} from '@/features/rpa/project-agent-ipc';
import { useUserWorkflows } from '@/features/rpa/recorder/hooks/useUserWorkflows';
import { deriveAvailableWorkflowSkillsFromSummaries } from '@/features/rpa/recorder/lib/derive-available-workflow-skills-from-summaries';
import { deriveWorkflowDetailPresentation } from '@/features/rpa/recorder/lib/derive-workflow-detail-presentation';
import { parseWorkflow } from '@/features/rpa/recorder/lib/parse-workflow';
import { useEvidenceIfuHref } from '@/features/settings/account/components/useIsEvidenceIfuVisible';
import { DEFAULT_LANDING_PAGE_SETTINGS_HASH } from '@/features/settings/display-controls/components/default-landing-page/landing-page-options';
import { useQueryDefaultLandingPage } from '@/features/settings/display-controls/components/default-landing-page/useDefaultLandingPageSetting';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { useHandleEditTemplate } from '@/features/templateBuilder/hooks/useHandleEditTemplate';
import { useVoiceAt } from '@/features/voice-assistant/workspace/voicePlacement';
import { useReferenceableLibraryDocuments } from '@/features/workflows/components/useWorkflowContextDocumentUpload';
import { useResolvableWorkflow, useWorkflows } from '@/features/workflows/hooks';
import { useOpenRoutineCreateChat } from '@/features/workflows/hooks/useOpenRoutineCreateChat';
import { writeHtmlAndMarkdownToClipboard } from '@/helpers/copyHelpers/copyHelpers-v2';
import { describeClipboardError } from '@/helpers/copyHelpers/describeClipboardError';
import { getDomains } from '@/helpers/getDomains';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useMutateStopRecordingFromPause } from '@/hooks/mutation/useMutateStopSessionFromPause';
import { useMutateUpdatePatientProfile } from '@/hooks/mutation/useMutateUpdatePatientProfile';
import { useMutateUpdateSessionRecordingStatus } from '@/hooks/mutation/useMutateUpdateSessionRecordingStatus';
import {
  cancelChatAttachmentUpload,
  useMutateDeleteChatAttachment,
  useMutateUploadChatAttachment,
} from '@/hooks/mutation/useMutateUploadChatAttachment';
import { NOTE_PUSH_TARGETS_QUERY_KEY } from '@/hooks/query/note-push-targets-query-key';
import { useQueryComputerUseNotePushTargets } from '@/hooks/query/useQueryComputerUseNotePushTargets';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQueryDocumentsBySessionId } from '@/hooks/query/useQueryDocumentsBySessionId';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';
import {
  useQuerySessionWindows,
  useSessionWindow,
  useSessionWindows,
} from '@/hooks/query/useQuerySessionWindows';
import { useQuerySessionsV2 } from '@/hooks/query/useQuerySessionsV2';
import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { useChatFileUpload } from '@/hooks/useChatFileUpload';
import { formatChatUploadErrorMessage } from '@/hooks/useChatFileUpload.messages';
import { SessionIdOverrideContext } from '@/hooks/useCurrentIds';
import { useEvidenceChatSurfaceGate } from '@/hooks/useEvidenceChatSurfaceGate';
import { useEvidenceChipsConfig } from '@/hooks/useEvidenceChipsConfig';
import { useFormDraftTitleState } from '@/hooks/useFormDraftTitleState';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useOpenSessionPage } from '@/hooks/useOpenSessionPage';
import { useRecording } from '@/hooks/useRecording/useRecording';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { isLogoutInFlight } from '@/lib/authInFlight';
import { cn } from '@/lib/cn';
import { FORM_FIELD_EDITOR_PORTS } from '@/lib/form-field-editor-ports';
import { isModalClosed } from '@/lib/modalClosedRules';
import { parseTimeStamp, parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import { ReactPdfDocument, ReactPdfPage } from '@/lib/pdf/react-pdf-loader';
import {
  openRecordingPauseAttempt,
  openRecordingStopAttempt,
} from '@/lib/telemetry/recordingControlOpeners';
import { hotkeysList } from '@/lists/hotkeys';
import { trackPatientRecapSourceSessionClicked } from '@/models/analytics/features/trackPatientRecapAnalytics';
import {
  resolveWorkBillingAnalyticsProps,
  trackWorkArtifactCreated,
  trackWorkArtifactExported,
  trackWorkEvent,
  WorkLaunchEventName,
} from '@/models/analytics/features/work';
import { trackDefaultLandingPageBannerViewed } from '@/models/analytics/features/work-home-analytics';
import { useTrackWorkArtifactCanvasActions } from '@/models/analytics/features/workArtifactCanvas';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { store, type RootState } from '@/store';
import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import {
  hasRpaErrorCode,
  listenRecordingExecutorStatus,
  listenRpaExecutorStatus,
  readComputerUseRefusal,
  RPA_DESKTOP_CONTROL_BLOCKED_ERROR_CODE,
  rpaBridge,
  RpaBridgeError,
} from '@/tauri/rpa';
import { buildRecorderUploadMetadata } from '@/tauri/rpa-recorder/upload-auth-context';
import { coerceWorkflowWirePayload } from '@/tauri/rpa-recorder/upload-types';
import { isTauri } from '@/tauri/utils';
import { resolveFormTemplateUrl } from '@/utils/formTemplate';
import { getPdfPageDimensions } from '@/utils/pdf-fields';
import { createLazyChunk } from '@/utils/retry-lazy';
import { reportBoundaryError } from '@/utils/telemetry/boundary-error';
import {
  configureWorkHomePorts,
  configureWorkHostPorts,
  type WorkHomePorts,
  type WorkHostPorts,
} from '@heidi/agents';
import { getDocumentType } from '@heidi/evidence/contracts';
import { useDocumentDownloadUrl } from '@heidi/evidence/data';
import { flattenSessionListPages, useQuerySessionsList } from '@heidi/session/data';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { lazy, useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';

/** Settings → Display controls, scrolled to and highlighting the Default page row. */
const DEFAULT_LANDING_PAGE_SETTINGS_HREF = `/settings/display-controls${DEFAULT_LANDING_PAGE_SETTINGS_HASH}`;

/** The landing picker, which is also what enables the desktop-target roster query. */
const useLandingDesktopTarget = () => useDesktopTargetSelection({ surface: 'landing' });

/** The boot-landing flag as a value + setter pair: the atom itself stays app-owned. */
const useHasBootLandedOnHome = (): [boolean, (next: boolean) => void] =>
  useAtom(hasBootLandedOnHomeAtom);

/** The mark-as-viewed write, unwrapped from the mutation object. */
const useMutateMarkModalAsViewedPort = () => useMutateMarkModalAsViewed().mutate;

/**
 * The two PostHog reads, each bound to its own key so the package never sees
 * one. `undefined` is a real answer — flags have not loaded — and is passed
 * through, because that is what the mount decision holds on.
 */
const useAgentHomeNavFlag = () => useFeatureFlagEnabled(AGENT_HOME_V1_FLAG_KEY);
const useObjectWorkspaceFlag = () =>
  useFeatureFlagEnabled(WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY);

export const workHomePorts: WorkHomePorts = {
  useOpenRoutineCreateChat,
  useLandingDesktopTarget,
  useDocumentDownloadUrl,
  pdf: { Document: ReactPdfDocument, Page: ReactPdfPage },
  getDocumentType,
  useIsDefaultLandingPageSettingEnabled,
  useDefaultLandingPageBannerWaitMs,
  useIsClinicalRecordingActive,
  useHasBootLandedOnHome,
  readBootPathname,
  isHomeBootPath,
  useQueryDefaultLandingPage,
  useMutateMarkModalAsViewed: useMutateMarkModalAsViewedPort,
  useSettingsModal,
  settingsHref: DEFAULT_LANDING_PAGE_SETTINGS_HREF,
  isModalClosed,
  parseTimeStampOrNull,
  trackButtonClicked,
  trackDefaultLandingPageBannerViewed,
  useAgentHomeNavFlag,
  useObjectWorkspaceFlag,
  usePosthogFlagsReady,
  useIsProjectsEnabled,
};

configureWorkHomePorts(workHomePorts);

const isEscAlreadyClaimed = (event: KeyboardEvent) => event.defaultPrevented;

/** The page-wide Esc stop, registered in the app's hotkey list. */
const useStopGeneratingHotkey: WorkHostPorts['shell']['useStopGeneratingHotkey'] = (
  onStop,
  enabled
) => {
  useHotkeys(
    hotkeysList.stopGenerating.hotkeys,
    () => onStop(),
    {
      scopes: hotkeysList.stopGenerating.scopes,
      preventDefault: true,
      ignoreEventWhen: isEscAlreadyClaimed,
      enabled,
    },
    [onStop]
  );
};

const useAreShortcutsDisabled = () => useAtomValue(isShortcutsDisabledAtom);

const useNavigate = (): ReturnType<WorkHostPorts['shell']['useNavigate']> => {
  const router = useRouter();
  return useCallback(
    async (href) => {
      await router.push(href);
    },
    [router]
  );
};

const useRouteRead = () => {
  const router = useRouter();
  return { isReady: router.isReady, pathname: router.pathname, query: router.query };
};

const useReplaceRouteQuery = () => {
  const router = useRouter();
  return useCallback(
    (query: Record<string, string | string[]>) => {
      void router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      });
    },
    [router]
  );
};

const useTimerSeconds = () => useSelector((s: RootState) => s.timerModel.seconds);

/** Auth-token poke. The body is the store subscription the attach loop had inline. */
export const subscribeAuthTokenChange = (cb: () => void): (() => void) =>
  store.subscribe(cb);

/**
 * Device bootstrap the overlay ran before a desktop start/resume. No-op off
 * desktop; the marker fills a slot the native recorder has not chosen yet.
 */
export const ensureDesktopRecordingDevices = async (): Promise<void> => {
  if (!isTauri()) return;
  let recordState = store.getState().scribeRecordModel;
  if (!recordState.tauriInputDeviceSelected || !recordState.tauriOutputDeviceSelected) {
    await store.dispatch.scribeRecordModel.getTauriDevices({
      updateSelectedDevices: true,
    });
    recordState = store.getState().scribeRecordModel;
  }
  const systemDefaultDevice = {
    name: SYSTEM_DEFAULT_DEVICE_MARKER,
    is_default: true,
    display_name: null,
    icon_data_url: null,
    is_communications_default: false,
  };
  if (!recordState.tauriInputDeviceSelected) {
    store.dispatch.scribeRecordModel.setTauriInputDevice(systemDefaultDevice);
  }
  if (!recordState.tauriOutputDeviceSelected) {
    store.dispatch.scribeRecordModel.setTauriOutputDevice(systemDefaultDevice);
  }
};

const useSetIngestFieldCandidates = () => useSetAtom(ingestFieldCandidatesAtom);
const useSetIngestNotePushHints = () => useSetAtom(ingestNotePushHintsAtom);
const useSetIngestNotePushRecorded = () => useSetAtom(ingestNotePushRecordedAtom);
const useSetClearCandidateMappings = () => useSetAtom(clearCandidateMappingsAtom);

const useCopilotRunContextByProject = () => useAtomValue(copilotRunContextByProjectAtom);

const readCopilotRunContextByProject = () =>
  getDefaultStore().get(copilotRunContextByProjectAtom);

const usePendingLetters = (sessionId: string) =>
  useAtomValue(pendingLettersAtomFamily(sessionId));

const openOverlayPauseAttempt = (sessionId: string) =>
  openRecordingPauseAttempt(sessionId, 'projects_overlay');
const openOverlayStopAttempt = (sessionId: string, fromPaused: boolean) =>
  openRecordingStopAttempt(sessionId, 'projects_overlay', fromPaused);

const useActiveSessionId = () => useAtomValue(activeSessionIdAtom);
const useSetActiveSessionId = () => useSetAtom(activeSessionIdAtom);
const useAgentRecordingSession = () => useAtom(agentRecordingSessionIdAtom);
const useRoutesToChronicle = () => useAtomValue(routesToChronicleAtom);

const useFormDraftTitle = () => useAtom(formDraftTitleAtom);
const useSetFormTemplateToEdit = () => useSetAtom(formTemplateToEditAtom);
const useCandidateMappings = () => useAtomValue(candidateMappingsAtom);
const useSetSectionOverride = () => useSetAtom(setSectionOverrideAtom);
const usePresentationAtom = () => useAtom(presentationAtom);
const useSetPresentationHistoryAtom = () => useSetAtom(presentationHistoryAtom);
const useSetPresentationOriginalAtom = () => useSetAtom(presentationOriginalAtom);
const useSetSelectedSlideIndexAtom = () => useSetAtom(selectedSlideIndexAtom);

const lazyNamed = (
  load: () => Promise<Record<string, unknown>>,
  exportName: string
): WorkHostPorts['session']['EmbeddingsPanel'] =>
  lazy(() =>
    load().then((mod) => ({
      default: mod[exportName] as WorkHostPorts['session']['EmbeddingsPanel'],
    }))
  );

const EmbeddingsPanel = lazy(
  () => import('@/container/ScribeTabs/TabPane/EmbeddingsPanel')
) as unknown as WorkHostPorts['session']['EmbeddingsPanel'];
const RegulatoryGuidelineBlockModal = lazyNamed(
  () =>
    import('@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-block-modal'),
  'RegulatoryGuidelineBlockModal'
);
const RegulatoryGuidelineWarningModal = lazyNamed(
  () =>
    import('@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-warning-modal'),
  'RegulatoryGuidelineWarningModal'
);
const PatientDocumentsSection = lazyNamed(
  () => import('@heidi/patients'),
  'PatientDocumentsSection'
);
const DrawerSections = lazyNamed(() => import('@heidi/patients'), 'DrawerSections');
const SessionHistorySection = lazyNamed(
  () => import('@heidi/patients'),
  'SessionHistorySection'
);
const SlideEditorPanel = lazyNamed(
  () => import('@/components/slide-builder/SlideEditorPanel'),
  'SlideEditorPanel'
);
const SlideUndoRedoButtons = lazyNamed(
  () => import('@/components/slide-builder/SlideToolbar'),
  'SlideUndoRedoButtons'
);
const SessionArtifactModalHosts = lazyNamed(
  () => import('@/container/session-page/v2/main/SessionArtifactModalHosts'),
  'SessionArtifactModalHosts'
);
const LinkedSessionTabsHost = lazyNamed(
  () => import('@/container/session-panel/LinkedSessionTabsHost'),
  'LinkedSessionTabsHost'
);
const SplitScreenSkeletonLoader = lazy(
  () => import('@/container/session-panel/split-screen/skeleton-loader')
) as WorkHostPorts['session']['SplitScreenSkeletonLoader'];
const ClinicalTasksPanelHostProvider = lazyNamed(
  () =>
    import('@/container/session-page/v2/session-panel/tasks-card/clinical-tasks-panel-host'),
  'ClinicalTasksPanelHostProvider'
);
const TemplateMarkdownPreview = lazyNamed(
  () => import('@/components/template-markdown-preview'),
  'TemplateMarkdownPreview'
);
const ChunkErrorMessage = lazyNamed(
  () => import('@/components/ui/chunk-error-message'),
  'ChunkErrorMessage'
);
const VoiceWindow = lazyNamed(
  () => import('@/features/voice-assistant/workspace/VoiceWindow'),
  'VoiceWindow'
);
const RoutineOverviewPane = lazyNamed(
  () => import('@/features/workflows/components/RoutineOverviewPane'),
  'RoutineOverviewPane'
);
const LinkedContextBadge = lazyNamed(
  () => import('@/components/ui/linked-context-badge'),
  'LinkedContextBadge'
);

const loadV2FocusedSessionArtifactPanel = () =>
  import('@/container/session-page/v2/main/v2-focused-session-artifact-panel').then(
    (mod) => ({
      default:
        mod.V2FocusedSessionArtifactPanel as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
    })
  );
const loadSessionPreviewArtifact = () =>
  import('@/container/session-page/v2/main/SessionPreviewArtifact').then((mod) => ({
    default:
      mod.SessionPreviewArtifact as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadShareTemplateModal = () =>
  import('@/components/modal/share-template-modal').then((mod) => ({
    default:
      mod.ShareTemplateModal as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadLetterTabPane = () =>
  import('@/features/integrations-letters/letter-sender-v2/components/letter-tab-pane').then(
    (mod) => ({
      default:
        mod.LetterTabPane as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
    })
  );

export const workHostPorts: WorkHostPorts = {
  ...workHomePorts,
  desktop: {
    emitProjectAgentRegistryPoke,
    listenProjectAgentRegistryPoke,
    useUserWorkflows: useUserWorkflows as WorkHostPorts['desktop']['useUserWorkflows'],
    deriveAvailableWorkflowSkillsFromSummaries:
      deriveAvailableWorkflowSkillsFromSummaries as unknown as WorkHostPorts['desktop']['deriveAvailableWorkflowSkillsFromSummaries'],
    rpaBridge,
    useSetIngestFieldCandidates:
      useSetIngestFieldCandidates as WorkHostPorts['desktop']['useSetIngestFieldCandidates'],
    useSetIngestNotePushHints:
      useSetIngestNotePushHints as WorkHostPorts['desktop']['useSetIngestNotePushHints'],
    useSetIngestNotePushRecorded:
      useSetIngestNotePushRecorded as WorkHostPorts['desktop']['useSetIngestNotePushRecorded'],
    useSetClearCandidateMappings,
    notePushTargetsQueryKey: NOTE_PUSH_TARGETS_QUERY_KEY,
    cancelComputerUseRun,
    useIsExecutorStopSignalled: useExecutorStopSignal,
    getAuthToken,
    isThisComputerTarget,
    useNativeInstanceId,
    useDesktopTargetRoster,
    getDomains,
    listenRecordingExecutorStatus:
      listenRecordingExecutorStatus as WorkHostPorts['desktop']['listenRecordingExecutorStatus'],
    listenRpaExecutorStatus:
      listenRpaExecutorStatus as WorkHostPorts['desktop']['listenRpaExecutorStatus'],
    buildRecorderUploadMetadata,
    useCopilotRunContextByProject,
    readCopilotRunContextByProject,
    releaseDesktopControlApprovalReservation,
    releaseDesktopControlApprovalReservationsForAgentRun,
    subscribeDesktopControlLease,
    useDesktopPermissionGate,
    hasRpaErrorCode,
    readComputerUseRefusal,
    RPA_DESKTOP_CONTROL_BLOCKED_ERROR_CODE,
    RpaBridgeError,
    deriveWorkflowDetailPresentation,
    parseWorkflow,
    coerceWorkflowWirePayload,
    DesktopBars,
    SYSTEM_DEFAULT_DEVICE_MARKER,
    // projects artifacts
    candidateMappingKey,
    useCandidateMappings,
    useSetSectionOverride:
      useSetSectionOverride as WorkHostPorts['desktop']['useSetSectionOverride'],
    useQueryComputerUseNotePushTargets:
      useQueryComputerUseNotePushTargets as WorkHostPorts['desktop']['useQueryComputerUseNotePushTargets'],
  },
  session: {
    useOfflinePendingServerDeleteIds,
    flattenSessionListPages,
    useQuerySessionsList,
    useQueryPatientProfiles,
    useQuerySessionsV2,
    usePrefetchSessionById,
    useSessionWindow: useSessionWindow as WorkHostPorts['session']['useSessionWindow'],
    useSessionWindows,
    useQuerySessionWindows,
    useActiveSessionId,
    useSetActiveSessionId,
    useAgentRecordingSession,
    useMutateStopRecordingFromPause,
    useMutateUpdateSessionRecordingStatus,
    useRecording,
    useTimerSeconds,
    useRoutesToChronicle,
    ensureDesktopRecordingDevices,
    openRecordingPauseAttempt: openOverlayPauseAttempt,
    openRecordingStopAttempt: openOverlayStopAttempt,
    // projects artifacts
    SessionIdOverrideContext,
    EmbeddingsPanel,
    useFormDraftTitle: useFormDraftTitle as WorkHostPorts['session']['useFormDraftTitle'],
    useSetFormTemplateToEdit:
      useSetFormTemplateToEdit as WorkHostPorts['session']['useSetFormTemplateToEdit'],
    useSaveFormTemplate:
      useSaveFormTemplate as WorkHostPorts['session']['useSaveFormTemplate'],
    FormViewType,
    RegulatoryGuidelineBlockModal,
    RegulatoryGuidelineWarningModal,
    useFormFieldEditorFlags:
      useFormFieldEditorFlags as WorkHostPorts['session']['useFormFieldEditorFlags'],
    useQueryTemplateById:
      useQueryTemplateById as WorkHostPorts['session']['useQueryTemplateById'],
    useFormDraftTitleState:
      useFormDraftTitleState as WorkHostPorts['session']['useFormDraftTitleState'],
    FORM_FIELD_EDITOR_PORTS,
    resolveFormTemplateUrl:
      resolveFormTemplateUrl as unknown as WorkHostPorts['session']['resolveFormTemplateUrl'],
    getPdfPageDimensions:
      getPdfPageDimensions as WorkHostPorts['session']['getPdfPageDimensions'],
    PatientDocumentsSection,
    DrawerSections,
    SessionHistorySection,
    useOpenPatientFullPage,
    useMutateUpdatePatientProfile:
      useMutateUpdatePatientProfile as unknown as WorkHostPorts['session']['useMutateUpdatePatientProfile'],
    usePresentationAtom:
      usePresentationAtom as WorkHostPorts['session']['usePresentationAtom'],
    useSetPresentationHistoryAtom:
      useSetPresentationHistoryAtom as WorkHostPorts['session']['useSetPresentationHistoryAtom'],
    useSetPresentationOriginalAtom:
      useSetPresentationOriginalAtom as WorkHostPorts['session']['useSetPresentationOriginalAtom'],
    useSetSelectedSlideIndexAtom:
      useSetSelectedSlideIndexAtom as WorkHostPorts['session']['useSetSelectedSlideIndexAtom'],
    usePresentation: usePresentation as WorkHostPorts['session']['usePresentation'],
    slideBuilderMessages:
      slideBuilderMessages as WorkHostPorts['session']['slideBuilderMessages'],
    SlideEditorPanel,
    SlideUndoRedoButtons,
    useSlideToolbarActions:
      useSlideToolbarActions as WorkHostPorts['session']['useSlideToolbarActions'],
    useSlideUndoRedoHotkeys:
      useSlideUndoRedoHotkeys as WorkHostPorts['session']['useSlideUndoRedoHotkeys'],
    sessionWindowsAtomFamily,
    SessionArtifactModalHosts,
    LinkedSessionTabsHost,
    loadSplitScreen,
    reportSplitScreenPreloadFailure,
    focusedArtifactTab:
      focusedArtifactTab as WorkHostPorts['session']['focusedArtifactTab'],
    toFocusedArtifactTarget:
      toFocusedArtifactTarget as WorkHostPorts['session']['toFocusedArtifactTarget'],
    SplitScreenSkeletonLoader,
    useOpenSessionPage:
      useOpenSessionPage as WorkHostPorts['session']['useOpenSessionPage'],
    loadV2FocusedSessionArtifactPanel,
    loadSessionPreviewArtifact,
    ClinicalTasksPanelHostProvider,
    TemplateMarkdownPreview,
    useHandleEditTemplate:
      useHandleEditTemplate as WorkHostPorts['session']['useHandleEditTemplate'],
    writeHtmlAndMarkdownToClipboard:
      writeHtmlAndMarkdownToClipboard as unknown as WorkHostPorts['session']['writeHtmlAndMarkdownToClipboard'],
    describeClipboardError,
    useQueryDocumentTemplates,
    loadShareTemplateModal,
    GET_SESSIONS_LIST,
    useQueryDocumentsBySessionId,
    usePatientProfileHost:
      usePatientProfileHost as WorkHostPorts['session']['usePatientProfileHost'],
  },
  evidence: {
    getResolvedShortcutsForMarket,
    buildSearchableShortcuts,
    useEvidenceChipsConfig,
    patientSexAge,
    cancelChatAttachmentUpload,
    useMutateDeleteChatAttachment,
    useMutateUploadChatAttachment,
    useChatFileUpload:
      useChatFileUpload as WorkHostPorts['evidence']['useChatFileUpload'],
    formatChatUploadErrorMessage,
    useEvidenceIfuHref,
    useEvidenceChatSurfaceGate,
    // projects artifacts
    LinkedContextBadge,
  },
  integrations: {
    usePendingLetters,
    // projects artifacts
    useLetter: useLetter as WorkHostPorts['integrations']['useLetter'],
    useQueryLetters: useQueryLetters as WorkHostPorts['integrations']['useQueryLetters'],
    useLetterSenderActions:
      useLetterSenderActions as WorkHostPorts['integrations']['useLetterSenderActions'],
    loadLetterTabPane,
  },
  routines: {
    useWorkflows,
    useReferenceableLibraryDocuments,
    // projects artifacts
    useVoiceAt: useVoiceAt as WorkHostPorts['routines']['useVoiceAt'],
    VoiceWindow,
    RoutineOverviewPane,
    useResolvableWorkflow,
  },
  shell: {
    useIsRpaRecorderEnabled,
    useIsWorkflowsEnabled,
    useIsAskHeidiSourcesShortcutsEnabled,
    useIsChatObjectRefsEnabled,
    isSlideBuilderEnabled: IS_SLIDE_BUILDER_ENABLED,
    isChatAttachmentsEnabled: IS_CHAT_ATTACHMENTS_ENABLED,
    parseTimeStamp,
    isLogoutInFlight,
    useAreShortcutsDisabled,
    creditAllowanceRefusal: {
      remember: rememberCreditAllowanceRefusal,
      clear: clearCreditAllowanceRefusal,
      nudge: nudgeCreditAllowanceRefusal,
      shouldSkipFollowUpSend: shouldSkipCreditAllowanceFollowUpSend,
    },
    useStopGeneratingHotkey,
    resolveWorkBillingAnalyticsProps,
    trackWorkEvent,
    workLaunchEventName: WorkLaunchEventName,
    subscribeAuthTokenChange,
    useNavigate,
    useRouteRead,
    useReplaceRouteQuery,
    useQueryKindeProperties,
    // projects artifacts
    IS_INSET_DESIGN_ENABLED,
    useIsAgentHomeV1Enabled,
    useIsPatientListEnabled,
    useIsAuthenticated,
    useTrackWorkArtifactCanvasActions,
    trackWorkArtifactCreated,
    trackWorkArtifactExported,
    trackPatientRecapSourceSessionClicked:
      trackPatientRecapSourceSessionClicked as WorkHostPorts['shell']['trackPatientRecapSourceSessionClicked'],
    cn: cn as WorkHostPorts['shell']['cn'],
    reportBoundaryError:
      reportBoundaryError as WorkHostPorts['shell']['reportBoundaryError'],
    ChunkErrorMessage,
    createLazyChunk:
      createLazyChunk as unknown as WorkHostPorts['shell']['createLazyChunk'],
  },
};

configureWorkHostPorts(workHostPorts);
